/** ---------------------------------------------------------------------------------------
 * --  LICENSE AGREEMENT -- *
 This software is licensed under the following terms:

 * -- GRANT OF LICENSE | RESTRICTIONS -- *
 Subject to the terms and conditions of this license, the copyright holders
 hereby grant you a non-exclusive, non-transferable, revocable license to use,
 copy, modify, and distribute the software.
 You are expressly prohibited from selling or commercially exploiting the software.
 Unauthorized use, reproduction, or distribution of the software, or any part
 thereof, is strictly prohibited.

 * -- AUTHORIZED USERS | INTELLECTUAL PROPERTY -- *
 This license is granted only to the individual or entity to whom the software is
 directly distributed by the copyright holders. Any use by other individuals or
 entities is unauthorized and in violation of this license. The software is
 protected by copyright and other intellectual property laws.
 The copyright holders retain all rights, title, and interest in the software.

 * -- GOVERNING LAW -- *
 This license is governed by and construed in accordance with the laws of Romania.
 Any legal action or proceeding related to this license shall be brought exclusively
 in the courts of Romania.

 [Weddify x Diaphan Stationery] © [2024] All rights reserved.
 -------------------------------------------------------------------------------------- **/

'use client';
import React from 'react';
import AppPlans from "@/components/plans/AppPlans";
import AppWedSnap from "@/components/wedsnap/AppWedSnap";
import AppButton from "@/components/button/AppButton";
import AppInvite from "@/components/invite/AppInvite";
import AppHook from "@/components/hook/AppHook";
import AppIVYPlanner from "@/components/planner/AppIVYPlanner";
import AppReviews from "@/components/reviews/AppReviews";
import AppAmbassador from "@/components/ambassador/AppAmbassador";
import AppFaq from "@/components/faq/AppFaq";
import AppReassurance from "@/components/reassurance/AppReassurance";
import AppStories from "@/components/stories/AppStories";
import AppPlacement from "@/components/placement/AppPlacement";
import AppStatistics from "@/components/statistics/AppStatistics";
import LazyVideo from "@/components/video/video";
import AppImageScroll from "@/components/random/AppImageScroll.tsx";
import {FaShoppingBag} from "react-icons/fa";
import {PiArrowBendRightDownThin} from "react-icons/pi";
import AppStatic from "@/utils/AppStatic";
import Image from 'next/image';
import mobileProposition from "@/assets/images/weddify-proposition-iphone.webp";
import homeHero from "@/assets/images/slides/slide-4.webp";

function AppHome() {

    const homepageFaq = [
        {
            title: AppStatic.appFaqFirstTitle.ro.translation,
            content: AppStatic.appFaqFirstText.ro.translation,
        },
        {
            title: AppStatic.appFaqSecondTitle.ro.translation,
            content: AppStatic.appFaqSecondText.ro.translation,
        },
        {
            title: AppStatic.appFaqThirdTitle.ro.translation,
            content: AppStatic.appFaqThirdText.ro.translation,
        },
        {
            title: AppStatic.appFaqFourthTitle.ro.translation,
            content: AppStatic.appFaqFourthText.ro.translation,
        },
        {
            title: AppStatic.appFaqIVYTitle.ro.translation,
            content: AppStatic.appFaqIVYText.ro.translation,
        },
        {
            title: AppStatic.appFaqIVYExtraTitle.ro.translation,
            content: AppStatic.appFaqIVYExtraText.ro.translation,
        },
        {
            title: AppStatic.appFaqFiveTitle.ro.translation,
            content: AppStatic.appFaqFiveText.ro.translation,
        },
        {
            title: AppStatic.appFaqEightTitle.ro.translation,
            content: AppStatic.appFaqEightText.ro.translation,
        },
        {
            title: AppStatic.appFaqNineTitle.ro.translation,
            content: AppStatic.appFaqNineText.ro.translation,
        },
        {
            title: AppStatic.appFaqTenTitle.ro.translation,
            content: AppStatic.appFaqTenText.ro.translation,
        },
        {
            title: AppStatic.appFaqElevenTitle.ro.translation,
            content: AppStatic.appFaqElevenText.ro.translation,
        },
        {
            title: AppStatic.appFaqFourteenTitle.ro.translation,
            content: AppStatic.appFaqFourteenText.ro.translation,
        },
        {
            title: AppStatic.appFaqFifteenTitle.ro.translation,
            content: AppStatic.appFaqFifteenText.ro.translation,
        },
        {
            title: AppStatic.appFaqTwelveTitle.ro.translation,
            content: AppStatic.appFaqTwelveText.ro.translation,
        },
        {
            title: AppStatic.appFaqThirteenTitle.ro.translation,
            content: AppStatic.appFaqThirteenText.ro.translation,
        }
    ];

    return (
        <div>
            <div className="relative h-auto md:mt-10">
                <div className="items-center w-full overflow-hidden h-screen">
					<div className="relative w-full overflow-hidden h-screen">
                        <AppImageScroll imageSource={homeHero}/>
					</div>
                    <div
                        className="absolute overflow-hidden z-10 flex items-center justify-center top-0 bottom-0 right-0 left-0 m-auto pt-10">
                        <div className="grid grid-cols-1 lg:grid-cols-2 justify-center items-center">
                            <div className="pl-5 pr-5 sm:pl-10 sm:pr-10 lg:pl-20 lg:pr-0 max-w-[738px]">
                                <div className="hidden md:block">
                                    <AppStories
                                        stories={[
                                            {url: "/clips/stories/weddify-story-ideea.mp4", type: "video"},
                                            {url: "/clips/stories/weddify-story-intro.mp4", type: "video"},
                                            {url: "/clips/stories/weddify-story-why.mp4", type: "video"},
                                        ]}
                                        storyOpeners={[
                                            {
                                                thumbnail: "/clips/stories/weddify-story-ideea-thumb.webp",
                                                label: "Weddify - Story"
                                            },
                                            {
                                                thumbnail: "/clips/stories/weddify-story-intro-thumb.webp",
                                                label: "Weddify - Intro"
                                            },
                                            {
                                                thumbnail: "/clips/stories/weddify-story-why-thumb.webp",
                                                label: "Weddify - Why"
                                            },
                                        ]}
                                    />
                                </div>
                                <h1 className="text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold uppercase app-header-line-height">{AppStatic.mainHeading.ro.translation}</h1>
                                <h3 className="text-xl lg:text-2xl">{AppStatic.mainSubHeading.ro.translation}</h3>
                                <div className="mt-8 flex flex-wrap gap-2">
                                    <AppButton data={{
                                        action: "scroll",
                                        id: "app-plans",
                                        name: AppStatic.mainViewTryFreeCta.ro.translation,
                                        icon: <FaShoppingBag className="f-2"/>,
                                    }}/>
                                    <AppButton data={{
                                        action: "scroll",
                                        id: "app-invite",
                                        extraClass: "bg-white! text-black!",
                                        name: AppStatic.mainDetailsCta.ro.translation,
                                        reverseTextAndIcon: true,
                                        icon: <PiArrowBendRightDownThin className="f-2"/>,
                                    }}/>
                                </div>
                            </div>
                            <div className="flex justify-center">
                                <Image alt="Weddify by Diaphan" className="hidden lg:flex w-[600px]" priority={true}
                                     src={mobileProposition}/>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="app-stories flex md:hidden mt-12 justify-center relative z-10">
                <AppStories
                    stories={[
                        {url: "/clips/stories/weddify-story-ideea.mp4", type: "video"},
                        {url: "/clips/stories/weddify-story-intro.mp4", type: "video"},
                        {url: "/clips/stories/weddify-story-why.mp4", type: "video"},
                    ]}
                    storyOpeners={[
                        {
                            thumbnail: "/clips/stories/weddify-story-ideea-thumb.webp",
                            label: "Weddify - Story"
                        },
                        {
                            thumbnail: "/clips/stories/weddify-story-intro-thumb.webp",
                            label: "Weddify - Intro"
                        },
                        {
                            thumbnail: "/clips/stories/weddify-story-why-thumb.webp",
                            label: "Weddify - Why"
                        },
                    ]}
                />
            </div>
            <div id="app-hook" className="pt-10 md:pt-16 pb-10 pl-5 pr-5 sm:pl-10 sm:pr-10 lg:pl-20 lg:pr-20 bg-white">
                <AppHook/>
            </div>
            <div id="app-invite" className="pt-20 pb-10 pl-5 pr-5 sm:pl-10 sm:pr-10 lg:pl-20 lg:pr-20 bg-gray-50">
                <AppInvite/>
            </div>
            <div id="app-wedsnap"
                 className="relative pt-20 pb-10 pl-5 pr-5 sm:pl-10 sm:pr-10 lg:pl-20 lg:pr-20 bg-[#232323]">
                <AppWedSnap/>
                <LazyVideo
                    className="absolute top-0 left-0 w-full h-full object-cover z-0 opacity-[0.3]"
                    autoPlay={true}
                    muted={true}
                    loop={true}
                    src="/clips/wedsnap/wedsnap-bg.mp4"
                    type="video/mp4"></LazyVideo>
            </div>
            <div id="app-planner"
                 className="pt-20 pb-10 pl-5 pr-5 sm:pl-10 sm:pr-10 lg:pl-20 lg:pr-20 bg-peach-base/10">
                <AppIVYPlanner/>
            </div>
            <div id="app-placement"
                 className="pt-20 pb-10 pl-5 pr-5 sm:pl-10 sm:pr-10 lg:pl-20 lg:pr-20 bg-[#AFB6B6]/30">
                <AppPlacement/>
            </div>
            <div id="app-reviews"
                 className="pt-20 pb-12 pl-5 pr-5 sm:pl-10 sm:pr-10 lg:pl-20 lg:pr-20 bg-white/10">
                <AppReviews/>
            </div>
            <div id="app-statistics"
                 className="pt-2 pb-10 pl-5 pr-5 sm:pl-10 sm:pr-10 lg:pl-20 lg:pr-20 bg-white/10">
                <AppStatistics/>
            </div>
            <div id="app-plans"
                 className="pt-10 pb-10 pl-5 pr-5 sm:pl-10 sm:pr-10 lg:pl-20 lg:pr-20 bg-white/10">
                <AppPlans/>
            </div>
            <div id="app-reassurance"
                 className="pt-2 pb-10 pl-5 pr-5 sm:pl-10 sm:pr-10 lg:pl-20 lg:pr-20 bg-white/10">
                <AppReassurance/>
            </div>
            <div id="app-faq"
                 className="pt-0 pb-10 pl-5 pr-5 sm:pl-10 sm:pr-10 lg:pl-20 lg:pr-20 bg-white/10">
                <AppFaq faqData={homepageFaq}/>
            </div>
            <div id="app-ambassador"
                 className="pt-12 pb-10 pl-5 pr-5 sm:pl-10 sm:pr-10 lg:pl-20 lg:pr-20  bg-[#232323]">
                <AppAmbassador/>
            </div>
        </div>
    );
}

export default AppHome;
